import dayjs from 'src/utils/dayjs';
import type { Dayjs } from 'src/utils/dayjs';
import DayPicker from 'react-day-picker/DayPicker';
import { DateToNameMap } from './RadioOptions';
type WeekIdObject = { year: string; week: string };

export const getWeekDays = (weekStart: Date) => {
  const days = [weekStart];
  for (let i = 1; i < 7; i += 1) {
    days.push(dayjs(weekStart).add(i, 'day').toDate());
  }
  return days;
};

export const weekIdParser = (weekId: string): WeekIdObject => {
  const specialChar = ['_', '-', ' '];
  const findSpecialChar = specialChar.filter((char) => weekId.includes(char));
  const weekIdArray = weekId.split(findSpecialChar[0]);
  const weekIdObject = { year: weekIdArray[0], week: weekIdArray[1] };
  return weekIdObject;
};

export const getWeekRange = (date: Date | Dayjs | undefined) => {
  return {
    from: dayjs(date).startOf('week').toDate(),
    to: dayjs(date).endOf('week').toDate(),
  };
};

export const NthWeekOfTheYear = (weekNumber: number, startOfWeek: boolean): Date => {
  let date = dayjs().startOf('year').toDate();
  if (weekNumber >= 1 || weekNumber <= 53) {
    if (startOfWeek) {
      date = dayjs()
        .startOf('year')
        .add((weekNumber - 1) * 7 - 1, 'day')
        .toDate();
    } else {
      date = dayjs()
        .startOf('year')
        .add(weekNumber * 7 - 1, 'day')
        .toDate();
    }
  }
  return date;
};

export function getSelectedIndex(options: { selected: boolean }[]) {
  return options.findIndex((option) => option.selected);
}

export function getSunday(date: Date) {
  const sunday = new Date(date.getTime());
  sunday.setDate(date.getDate() - date.getDay());
  return sunday;
}

export function getWeekLabel(date: Date, validNames: DateToNameMap) {
  const validNamesObject = { ...validNames.start_date, ...validNames.end_date };
  const { from } = getWeekRange(date);
  const dateKey = dayjs(from).format('YYYY-MM-DD');
  return validNamesObject[dateKey] || dateKey;
}

// If subtracting a year off of the assortment day doesn't result in a Sunday
// get the closest one.
export const getClosestSunday = (date: Dayjs) => {
  switch (date.day()) {
    case 0:
    case 1:
    case 2:
      return date.day(0);
    case 3:
    case 4:
    case 5:
    case 6:
      return date.day(7);
  }
  return date;
};

export function getDateFromWeek(week: string, validDays: object): Date | undefined {
  const foundDateString = Object.keys(validDays).find((date) => (validDays as Record<string, any>)[date] === week);
  return foundDateString ? dayjs(foundDateString).toDate() : undefined;
}

export function isDayInRange(day: Date, allowFromDate: Date, allowToDate: Date) {
  return DayPicker.DateUtils.isDayInRange(day, { from: allowFromDate, to: allowToDate });
}

export function isDayBefore(day: Date, previousOptionDate: Date) {
  return DayPicker.DateUtils.isDayBefore(day, previousOptionDate);
}

export function isDayAfter(day: Date, allowFromDate: Date) {
  return DayPicker.DateUtils.isDayAfter(day, allowFromDate);
}

export function isOutsideViewport(element: HTMLElement, buffer: number) {
  const viewPortHeight = window.innerHeight || window.document.documentElement.clientHeight;
  const viewPortWidth = window.innerWidth || window.document.documentElement.clientWidth;
  const { bottom, right } = element.getBoundingClientRect();
  const out = {
    // Add top and left when/if needed.
    bottom: bottom - viewPortHeight + buffer > 0,
    right: right - viewPortWidth + buffer > 0,
  };
  return out;
}

export function pixelsToNumber(propertyKey: string, computedStyles: CSSStyleDeclaration) {
  const dayPickerCSSTop = computedStyles.getPropertyValue(propertyKey);
  const [computedCSSTop] = dayPickerCSSTop.split('px');
  return Number(computedCSSTop);
}
